/**
 * ROLE CAPABILITY MATRIX - Single Source of Truth
 *
 * This file defines ALL permissions for ALL roles in AlphaSignal.
 * Every route guard, feature gate, and RLS reference should derive
 * permissions from this centralized matrix.
 *
 * NEVER duplicate role logic elsewhere. Import from this file.
 *
 * @security This is a security-critical file. Changes require review.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { OrgRole } from './roles';
import { ROUTES } from '@/lib/routes';

// =============================================================================
// Capability Definitions
// =============================================================================

export type Capability =
  // Data Access
  | 'read:players' | 'write:players' | 'delete:players'
  | 'read:wellness' | 'write:wellness'
  | 'read:load' | 'write:load'
  | 'read:injuries' | 'write:injuries' | 'delete:injuries'
  | 'read:gps' | 'write:gps'
  | 'read:fitness' | 'write:fitness'
  | 'read:cognitive' | 'write:cognitive'
  | 'read:metabolic' | 'write:metabolic'
  | 'read:body_comp' | 'write:body_comp'
  | 'read:speed' | 'write:speed'
  | 'read:matches' | 'write:matches'
  | 'read:rtp_cases' | 'write:rtp_cases' | 'advance:rtp_stage'
  | 'read:reports' | 'write:reports'
  | 'read:scouting' | 'write:scouting'
  // Analytics & Insights
  | 'view:dashboard' | 'view:analytics' | 'view:insights'
  | 'view:forecasts' | 'view:comparisons' | 'view:risk_forecast'
  // Administration
  | 'manage:org_settings' | 'manage:team_settings' | 'manage:squad_settings'
  | 'manage:users' | 'manage:thresholds' | 'manage:integrations'
  // Uploads & Imports
  | 'upload:csv' | 'upload:bulk'
  // Actions & Tasks
  | 'create:actions' | 'assign:actions' | 'view:activity_feed'
  // Decision Overrides
  | 'override:guardrails' | 'dismiss:risk_flags'
  // Player Portal (self-service)
  | 'submit:own_wellness' | 'view:own_data'
  // ── Tier 7.2a additions ──────────────────────────────────────
  // New module capabilities
  | 'read:nutrition' | 'write:nutrition'
  | 'read:recovery' | 'write:recovery'
  | 'read:illness' | 'write:illness'
  | 'read:hrv' | 'write:hrv'
  | 'read:periodisation' | 'write:periodisation'
  // Workflow capabilities
  | 'view:workflows' | 'manage:workflows' | 'acknowledge:workflow_tasks'
  // Permission management — locked, admin only
  | 'manage:permissions'
  // Operations capabilities
  | 'manage:attendance' | 'manage:scheduling' | 'view:logistics'
  // Extended role capabilities
  | 'write:physio_notes';

/**
 * Capabilities that cannot be granted or revoked via role_permission_overrides
 * regardless of org_admin action. Always evaluated from system default in
 * ROLE_CAPABILITIES. Mirrored in the SQL function resolve_role_capability.
 */
export const LOCKED_CAPABILITIES: Capability[] = [
  'manage:permissions',
  'manage:users',
];

// =============================================================================
// Route Permission Mapping
// =============================================================================

export const ROUTE_CAPABILITIES: Record<string, Capability[]> = {
  [ROUTES.DASHBOARD]: ['view:dashboard'],
  [ROUTES.PLAYERS]: ['read:players'],
  [ROUTES.ANALYTICS]: ['view:analytics'],
  [ROUTES.INSIGHTS]: ['view:insights'],

  [ROUTES.LOAD_MONITORING]: ['read:gps'],
  [ROUTES.PERFORMANCE]: ['read:gps'],
  [ROUTES.FITNESS_TESTING]: ['read:fitness'],
  [ROUTES.SPEED_TESTING]: ['read:speed'],
  [ROUTES.COGNITIVE_TESTING]: ['read:cognitive'],
  [ROUTES.BODY_COMPOSITION]: ['read:body_comp'],
  [ROUTES.METABOLIC_TESTING]: ['read:metabolic'],
  [ROUTES.WELLNESS]: ['read:wellness'],

  [ROUTES.INJURY_TRACKING]: ['read:injuries'],
  [ROUTES.INJURIES]: ['read:injuries'],
  [ROUTES.RETURN_TO_PLAY]: ['read:rtp_cases'],
  [ROUTES.RTP]: ['read:rtp_cases'],

  [ROUTES.MATCHES]: ['read:matches'],
  [ROUTES.MATCH_MODE_LEGACY]: ['read:matches'],
  [ROUTES.LEGACY_PLANNER]: ['write:matches'],
  [ROUTES.PLANNED_SESSIONS]: ['read:matches'],
  [ROUTES.SCENARIO_PLANNER]: ['view:forecasts', 'read:players'],
  [ROUTES.RISK_FORECAST]: ['view:risk_forecast'],

  [ROUTES.UPLOADS]: ['upload:csv'],
  [ROUTES.DATA_MANAGEMENT]: ['upload:bulk'],

  // SCOUTING capability removed — feature cut from product scope.
  [ROUTES.SQUAD_GAPS]: ['read:scouting', 'view:analytics'],

  // AI_REPORTS retired — feature removed
  [ROUTES.WEEKLY_REPORT]: ['read:reports'],

  [ROUTES.ACTIONS]: ['create:actions'],
  [ROUTES.COLLABORATION]: ['view:activity_feed'],

  [ROUTES.SETTINGS_ORGANIZATION]: ['manage:org_settings'],
  [ROUTES.SETTINGS_TEAM]: ['manage:team_settings'],
  [ROUTES.SETTINGS_SQUADS]: ['manage:squad_settings'],
  
  [ROUTES.SETTINGS_USERS]: ['manage:users'],
  [ROUTES.SETTINGS_INTEGRATIONS]: ['manage:integrations'],
  [ROUTES.SETTINGS_PERMISSIONS]: ['manage:permissions'],
  [ROUTES.WORKFLOWS]: ['view:workflows'],

  [ROUTES.PLAYER_CHECK_IN]: ['submit:own_wellness'],
  [ROUTES.PLAYER_HISTORY]: ['view:own_data'],
};

// =============================================================================
// Role Capability Matrix
// =============================================================================

const ALL_ADMIN_CAPABILITIES: Capability[] = [
  'read:players', 'write:players', 'delete:players',
  'read:wellness', 'write:wellness',
  'read:load', 'write:load',
  'read:injuries', 'write:injuries', 'delete:injuries',
  'read:gps', 'write:gps',
  'read:fitness', 'write:fitness',
  'read:cognitive', 'write:cognitive',
  'read:metabolic', 'write:metabolic',
  'read:body_comp', 'write:body_comp',
  'read:speed', 'write:speed',
  'read:matches', 'write:matches',
  'read:rtp_cases', 'write:rtp_cases', 'advance:rtp_stage',
  'read:reports', 'write:reports',
  'read:scouting', 'write:scouting',
  'view:dashboard', 'view:analytics', 'view:insights',
  'view:forecasts', 'view:comparisons', 'view:risk_forecast',
  'manage:org_settings', 'manage:team_settings', 'manage:squad_settings',
  'manage:users', 'manage:thresholds', 'manage:integrations',
  'upload:csv', 'upload:bulk',
  'create:actions', 'assign:actions', 'view:activity_feed',
  'override:guardrails', 'dismiss:risk_flags',
  // Tier 7.2a
  'read:nutrition', 'write:nutrition',
  'read:recovery', 'write:recovery',
  'read:illness', 'write:illness',
  'read:hrv', 'write:hrv',
  'read:periodisation', 'write:periodisation',
  'view:workflows', 'manage:workflows', 'acknowledge:workflow_tasks',
  'manage:permissions',
  'manage:attendance', 'manage:scheduling', 'view:logistics',
  'write:physio_notes',
];

export const ROLE_CAPABILITIES: Record<OrgRole, Capability[]> = {
  super_admin: ALL_ADMIN_CAPABILITIES,
  org_admin: ALL_ADMIN_CAPABILITIES,

  performance: [
    'read:players', 'write:players',
    'read:wellness', 'write:wellness',
    'read:load', 'write:load',
    'read:injuries', 'write:injuries',
    'read:gps', 'write:gps',
    'read:fitness', 'write:fitness',
    'read:cognitive', 'write:cognitive',
    'read:metabolic', 'write:metabolic',
    'read:body_comp', 'write:body_comp',
    'read:speed', 'write:speed',
    'read:matches', 'write:matches',
    'read:rtp_cases',
    'read:reports', 'write:reports',
    'read:scouting',
    'view:dashboard', 'view:analytics', 'view:insights',
    'view:forecasts', 'view:comparisons', 'view:risk_forecast',
    'upload:csv', 'upload:bulk',
    'create:actions', 'assign:actions', 'view:activity_feed',
    'dismiss:risk_flags',
    // Tier 7.2a additions for performance
    'read:nutrition', 'write:nutrition',
    'read:recovery', 'write:recovery',
    'read:illness',
    'read:hrv', 'write:hrv',
    'read:periodisation', 'write:periodisation',
    'view:workflows', 'acknowledge:workflow_tasks',
  ],

  medical: [
    'read:players',
    'read:wellness', 'write:wellness',
    'read:load',
    'read:injuries', 'write:injuries', 'delete:injuries',
    'read:gps', 'read:fitness', 'read:cognitive', 'read:metabolic',
    'read:body_comp', 'read:speed', 'read:matches',
    'read:rtp_cases', 'write:rtp_cases', 'advance:rtp_stage',
    'read:reports',
    'view:dashboard', 'view:analytics', 'view:insights',
    'view:forecasts', 'view:comparisons', 'view:risk_forecast',
    'create:actions', 'view:activity_feed',
    'override:guardrails',
    // Tier 7.2a
    'read:nutrition', 'read:recovery',
    'read:illness', 'write:illness',
    'read:hrv',
    'view:workflows', 'acknowledge:workflow_tasks',
  ],

  coach: [
    'read:players',
    'read:wellness', 'read:load',
    'read:injuries',
    'read:gps', 'read:fitness', 'read:cognitive', 'read:metabolic',
    'read:body_comp', 'read:speed',
    'read:matches', 'write:matches',
    'read:rtp_cases',
    'read:reports', 'read:scouting',
    'view:dashboard', 'view:analytics', 'view:insights', 'view:comparisons',
    'create:actions', 'view:activity_feed',
    // Tier 7.2a
    'read:nutrition', 'read:recovery', 'read:illness',
    'view:workflows', 'acknowledge:workflow_tasks',
  ],

  // ── New roles (Tier 7.2a) ────────────────────────────────────────────────
  nutritionist: [
    'read:players',
    'read:wellness',
    'read:nutrition', 'write:nutrition',
    'read:recovery', 'write:recovery',
    'read:fitness', 'read:load',
    'read:reports',
    'view:dashboard', 'view:analytics', 'view:insights',
    'submit:own_wellness', 'view:own_data',
    'acknowledge:workflow_tasks', 'view:workflows',
    'create:actions', 'view:activity_feed', 'upload:csv',
  ],

  physio: [
    'read:players',
    'read:wellness', 'write:wellness',
    'read:injuries', 'write:injuries',
    'read:rtp_cases', 'write:rtp_cases', 'advance:rtp_stage',
    'write:physio_notes',
    'read:nutrition', 'read:recovery', 'write:recovery',
    'read:illness', 'write:illness',
    'read:load', 'read:fitness', 'read:gps',
    'read:metabolic', 'read:reports',
    'view:dashboard', 'view:analytics', 'view:insights', 'view:forecasts',
    'view:risk_forecast',
    'submit:own_wellness', 'view:own_data',
    'acknowledge:workflow_tasks', 'view:workflows',
    'create:actions', 'assign:actions', 'view:activity_feed',
    'upload:csv', 'dismiss:risk_flags',
  ],

  analyst: [
    'read:players',
    'read:wellness', 'read:load',
    'read:gps', 'read:fitness',
    'read:cognitive', 'read:metabolic', 'read:body_comp',
    'read:speed', 'read:matches', 'read:rtp_cases',
    'read:reports', 'read:scouting',
    'view:dashboard', 'view:analytics', 'view:insights',
    'view:forecasts', 'view:comparisons', 'view:risk_forecast',
    'submit:own_wellness', 'view:own_data',
    'view:workflows', 'acknowledge:workflow_tasks',
    'view:activity_feed', 'upload:csv',
  ],

  operations: [
    'read:players',
    'read:matches',
    'manage:attendance', 'manage:scheduling', 'view:logistics',
    'view:dashboard',
    'submit:own_wellness', 'view:own_data',
    'view:workflows', 'acknowledge:workflow_tasks',
    'view:activity_feed',
  ],

  player_viewer: ['submit:own_wellness', 'view:own_data'],
  parent: ['view:own_data'],
};

// =============================================================================
// Capability Check Functions
// =============================================================================

export function roleHasCapability(role: OrgRole | null, capability: Capability): boolean {
  if (!role) return false;
  return ROLE_CAPABILITIES[role]?.includes(capability) ?? false;
}

export function roleHasAllCapabilities(role: OrgRole | null, capabilities: Capability[]): boolean {
  if (!role) return false;
  return capabilities.every(cap => roleHasCapability(role, cap));
}

export function roleHasAnyCapability(role: OrgRole | null, capabilities: Capability[]): boolean {
  if (!role) return false;
  return capabilities.some(cap => roleHasCapability(role, cap));
}

export function getCapabilitiesForRole(role: OrgRole): Capability[] {
  return ROLE_CAPABILITIES[role] || [];
}

export function canAccessRoute(role: OrgRole | null, route: string): boolean {
  if (role === 'super_admin' || role === 'org_admin') return true;
  const requiredCapabilities = ROUTE_CAPABILITIES[route];
  if (!requiredCapabilities) return true;
  return roleHasAllCapabilities(role, requiredCapabilities);
}

export function getAccessibleRoutes(role: OrgRole): string[] {
  return Object.entries(ROUTE_CAPABILITIES)
    .filter(([_, caps]) => roleHasAllCapabilities(role, caps))
    .map(([route]) => route);
}

/**
 * Async route access check that respects org-specific permission overrides
 * stored in role_permission_overrides. Use this where async is acceptable
 * (e.g. effect-driven route guards). The synchronous canAccessRoute remains
 * for navigation rendering where async is not appropriate.
 */
export async function canAccessRouteWithOverrides(
  route: string,
  role: OrgRole,
  orgId: string,
  supabaseClient: SupabaseClient,
): Promise<boolean> {
  if (role === 'super_admin' || role === 'org_admin') return true;
  const required = ROUTE_CAPABILITIES[route];
  if (!required) return true;
  for (const cap of required) {
    const ok = await roleHasCapabilityWithOverrides(role, cap, orgId, supabaseClient);
    if (!ok) return false;
  }
  return true;
}

/**
 * Resolve a role's capability against an org-specific override stored in
 * role_permission_overrides. Locked capabilities always use the system default.
 *
 * NOTE: Call sites to be updated in Tier 7 Prompt 7.2b — permission override
 * Settings UI. This function exists so 7.2b can wire it in without further
 * capability-system changes.
 */
export async function roleHasCapabilityWithOverrides(
  role: OrgRole,
  capability: Capability,
  orgId: string,
  supabaseClient: SupabaseClient,
): Promise<boolean> {
  if (LOCKED_CAPABILITIES.includes(capability)) {
    return roleHasCapability(role, capability);
  }
  const { data } = await supabaseClient.rpc('resolve_role_capability', {
    p_org_id: orgId,
    p_role: role,
    p_capability: capability,
  });
  if (data === null || data === undefined) {
    return roleHasCapability(role, capability);
  }
  return data as boolean;
}

// =============================================================================
// Table Permission Matrix (RLS reference)
// =============================================================================

export const TABLE_PERMISSIONS: Record<string, {
  select: OrgRole[]; insert: OrgRole[]; update: OrgRole[]; delete: OrgRole[];
}> = {
  players: {
    select: ['super_admin', 'org_admin', 'performance', 'medical', 'coach', 'nutritionist', 'physio', 'analyst', 'operations'],
    insert: ['super_admin', 'org_admin', 'performance'],
    update: ['super_admin', 'org_admin', 'performance'],
    delete: ['super_admin', 'org_admin'],
  },
  wellness_daily: {
    select: ['super_admin', 'org_admin', 'performance', 'medical', 'coach', 'nutritionist', 'physio', 'analyst'],
    insert: ['super_admin', 'org_admin', 'performance', 'medical', 'physio'],
    update: ['super_admin', 'org_admin', 'performance', 'medical', 'physio'],
    delete: ['super_admin', 'org_admin'],
  },
  injuries: {
    select: ['super_admin', 'org_admin', 'medical', 'performance', 'coach', 'physio'],
    insert: ['super_admin', 'org_admin', 'performance', 'medical', 'physio'],
    update: ['super_admin', 'org_admin', 'medical', 'physio'],
    delete: ['super_admin', 'org_admin', 'medical'],
  },
  rtp_cases: {
    select: ['super_admin', 'org_admin', 'performance', 'medical', 'coach', 'physio', 'analyst'],
    insert: ['super_admin', 'org_admin', 'medical', 'physio'],
    update: ['super_admin', 'org_admin', 'medical', 'physio'],
    delete: ['super_admin', 'org_admin'],
  },
  daily_loads: {
    select: ['super_admin', 'org_admin', 'performance', 'medical', 'coach', 'nutritionist', 'physio', 'analyst'],
    insert: ['super_admin', 'org_admin', 'performance'],
    update: ['super_admin', 'org_admin', 'performance'],
    delete: ['super_admin', 'org_admin'],
  },
  risk_flags: {
    select: ['super_admin', 'org_admin', 'performance', 'medical', 'coach', 'nutritionist', 'physio', 'analyst'],
    insert: ['super_admin', 'org_admin', 'performance'],
    update: ['super_admin', 'org_admin', 'performance', 'medical', 'physio'],
    delete: ['super_admin', 'org_admin'],
  },
  system_logs: {
    select: ['super_admin', 'org_admin'],
    insert: ['super_admin', 'org_admin', 'performance', 'medical', 'coach', 'nutritionist', 'physio', 'analyst', 'operations'],
    update: [],
    delete: [],
  },
  profiles: {
    select: ['super_admin', 'org_admin', 'performance', 'medical', 'coach', 'nutritionist', 'physio', 'analyst', 'operations'],
    insert: ['super_admin', 'org_admin'],
    update: ['super_admin', 'org_admin'],
    delete: ['super_admin'],
  },
  travel_segments: {
    select: ['super_admin', 'org_admin', 'performance', 'medical', 'coach', 'physio', 'operations'],
    insert: ['super_admin', 'org_admin', 'performance', 'medical', 'operations'],
    update: ['super_admin', 'org_admin', 'performance', 'medical', 'operations'],
    delete: ['super_admin', 'org_admin'],
  },
  readiness_forecast: {
    select: ['super_admin', 'org_admin', 'performance', 'medical', 'coach', 'physio', 'analyst'],
    insert: ['super_admin', 'org_admin', 'performance'],
    update: ['super_admin', 'org_admin', 'performance'],
    delete: ['super_admin', 'org_admin'],
  },
};

// =============================================================================
// Helpers
// =============================================================================

const STAFF_ROLE_LIST: OrgRole[] = [
  'super_admin', 'org_admin', 'performance', 'medical', 'coach',
  'nutritionist', 'physio', 'analyst', 'operations',
];

export function isStaffRole(role: OrgRole | null): boolean {
  if (!role) return false;
  return STAFF_ROLE_LIST.includes(role);
}

export function isAdminRole(role: OrgRole | null): boolean {
  if (!role) return false;
  return role === 'super_admin' || role === 'org_admin';
}
